import { z } from "zod";

import { defineEvents } from "~/analytics/factory";

/**
 * Analytics events for the post-signup onboarding checklist.
 * Namespace: "onboarding-checklist"
 * "step_type" corresponds to the API-driven step type keys:
 *   theme_step | brand_step | integrations_step | email_templates_step |
 *   campaigns_step | consent_pages_step | widgets_step | sales_agent_step |
 *   dkim_domain_step | account_plan_step | ad_campaigns_step
 */
export const events = defineEvents("onboarding-checklist", {
  /** User opened the onboarding checklist drawer via the "Continue Setup" button. */
  opened: z.object({}),

  /** User closed the onboarding checklist drawer. */
  closed: z.object({}),

  /** User expanded (opened accordion for) a checklist step. */
  step_viewed: z.object({
    step_type: z.string(),
    step_status: z.string(),
    /** Whether the step was enabled (dependencies met) when viewed */
    is_enabled: z.boolean(),
  }),

  /**
   * User chose a creation flow from FlowPickerActions on a pending step.
   * Applies to: theme, brand, email_templates, campaigns, consent_pages, widgets, sales_agent, ad_campaigns
   */
  flow_selected: z.object({
    step_type: z.string(),
    /** "assisted" | "manual" | "action" */
    flow: z.string(),
    /** Entity ID of the template/library item being adapted, if applicable */
    item_id: z.string().optional(),
  }),

  /**
   * User clicked the platform install button on a pending integrations step.
   */
  integration_install_clicked: z.object({
    /** "shopify" | "bigcommerce" | "woocommerce" */
    provider: z.string(),
  }),

  /**
   * User clicked "View" on a completed step to navigate to the entity.
   */
  step_view_clicked: z.object({
    step_type: z.string(),
    entity_id: z.string(),
  }),

  /**
   * All checklist steps are done — setup is complete.
   * Fires when isDone transitions to true.
   */
  completed: z.object({
    type: z.string().optional(),
  }),
});

export type Events = typeof events;
